import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The settings page's section index (D278): on a wide screen, a list of the
 * page's sections beside them, so a laboratory finds "Bank details" or
 * "Numbering" without scrolling past everything else. On a phone the sections
 * simply stack and the index is not drawn.
 *
 * The index is navigation only. It changes no value and saves nothing; each
 * group still saves exactly as it did — the laboratory profile with its one
 * Save, every separately saved panel with its own.
 */
export interface SettingsIndexGroup {
  /** Already translated. */
  readonly label: string;
  readonly items: readonly { readonly id: string; readonly label: string }[];
}

export function SettingsIndex({ groups }: { readonly groups: readonly SettingsIndexGroup[] }) {
  const { t } = useTranslation();

  // Scroll in place rather than writing a hash into the address: the address
  // stays the page's, and a section heading is brought to the top.
  const go = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    const target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView?.({ block: 'start' });
    const focusable = target.querySelector<HTMLElement>('h2');
    if (focusable) {
      focusable.setAttribute('tabindex', '-1');
      focusable.focus({ preventScroll: true });
    }
  };

  return (
    <nav
      aria-label={t('settings.index.label')}
      className="hidden rounded-large border border-default-200 bg-white p-2 lg:sticky lg:top-4 lg:block"
    >
      {groups.map((group) => (
        <div key={group.label} className="mb-2 last:mb-0">
          <p className="px-2 pb-1 pt-1.5 text-micro font-semibold text-default-600">{group.label}</p>
          <ul className="flex flex-col">
            {group.items.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  onClick={(event) => go(event, item.id)}
                  className="block rounded-medium px-2 py-1.5 text-body text-default-800 no-underline hover:bg-default-100 hover:text-default-900 focus-visible:outline-2 focus-visible:outline-primary"
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/** A section the index points at: an anchor that leaves room above it. */
export function SettingsSection({ id, children }: { readonly id: string; readonly children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-4">
      {children}
    </div>
  );
}
